import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import DownOutlined from '@ant-design/icons/DownOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import UpOutlined from '@ant-design/icons/UpOutlined';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import Empty from 'antd/es/empty';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import InputNumber from 'antd/es/input-number';
import Modal from 'antd/es/modal';
import Select from 'antd/es/select';
import Space from 'antd/es/space';
import Switch from 'antd/es/switch';
import Tag from 'antd/es/tag';
import Typography from 'antd/es/typography';
import React, { useState } from 'react';
import styled from 'styled-components';

import type { JSONSchemaType } from '@mai/types';

const { Text } = Typography;
const { TextArea } = Input;

const StyledCard = styled(Card)`
  ${({ theme }) => `
  .ant-card-body {
    padding: ${theme.paddingLG}px;
  }
`}
`;

const PropertyItem = styled.div`
  ${({ theme }) => `
  border: ${theme.lineWidth}px ${theme.lineType} ${theme.colorBorderSecondary};
  border-radius: ${theme.borderRadius}px;
  margin-bottom: ${theme.marginMD}px;
  overflow: hidden;
  transition: all ${theme.motionDurationSlow};
  
  &:hover {
    border-color: ${theme.colorPrimary};
    box-shadow: ${theme.boxShadowTertiary};
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`}
`;

const PropertyHeader = styled.div`
  ${({ theme }) => `
  padding: ${theme.paddingSM}px ${theme.padding}px;
  background: ${theme.colorBgTextHover};
  border-bottom: ${theme.lineWidth}px ${theme.lineType} ${theme.colorBorderSecondary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  
  &:hover {
    background: ${theme.colorBgTextActive};
  }
`}
`;

const PropertyContent = styled.div`
  ${({ theme }) => `
  padding: ${theme.padding}px;
  background: ${theme.colorBgContainer};
`}
`;

const TypeBadge = styled(Tag)`
  ${({ theme }) => `
  margin: 0;
  font-size: ${theme.fontSizeSM}px;
`}
`;

const RequiredBadge = styled(Tag)`
  ${({ theme }) => `
  margin: 0;
  font-size: ${theme.fontSizeSM}px;
`}
`;

const HelpText = styled(Text)`
  ${({ theme }) => `
  font-size: ${theme.fontSizeSM}px;
  color: ${theme.colorTextDescription};
  display: block;
  margin-top: ${theme.marginXXS}px;
`}
`;

const EmptyContainer = styled.div`
  ${({ theme }) => `
  padding: ${theme.paddingLG * 2}px 0;
`}
`;

interface PropertyManagerProps {
  properties: Record<string, JSONSchemaType>;
  required: string[];
  onAdd: (name: string, schema: JSONSchemaType) => void;
  onUpdate: (name: string, schema: JSONSchemaType) => void;
  onDelete: (name: string) => void;
  onToggleRequired: (name: string) => void;
}

const typeOptions = [
  { value: 'string', label: 'Text', color: 'green' },
  { value: 'number', label: 'Number', color: 'blue' },
  { value: 'integer', label: 'Integer', color: 'cyan' },
  { value: 'boolean', label: 'Boolean', color: 'purple' },
  { value: 'array', label: 'Array', color: 'orange' },
  { value: 'object', label: 'Object', color: 'red' },
];

const stringFormatOptions = [
  { value: '', label: 'None' },
  { value: 'date', label: 'Date' },
  { value: 'date-time', label: 'Date & Time' },
  { value: 'email', label: 'Email' },
  { value: 'uri', label: 'URL' },
  { value: 'uuid', label: 'UUID' },
];

export const PropertyManager: React.FC<PropertyManagerProps> = ({
  properties,
  required,
  onAdd,
  onUpdate,
  onDelete,
  onToggleRequired,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newPropertyName, setNewPropertyName] = useState('');
  const [newPropertyType, setNewPropertyType] = useState('string');
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const handleAddProperty = () => {
    if (newPropertyName && !properties[newPropertyName]) {
      const defaultSchema: JSONSchemaType = {
        type: newPropertyType as JSONSchemaType['type'],
      };

      // Add default values based on type
      if (newPropertyType === 'string') {
        defaultSchema.description = '';
      } else if (
        newPropertyType === 'number' ||
        newPropertyType === 'integer'
      ) {
        defaultSchema.description = '';
      } else if (newPropertyType === 'array') {
        defaultSchema.items = { type: 'string' };
      } else if (newPropertyType === 'object') {
        defaultSchema.properties = {};
      }

      onAdd(newPropertyName, defaultSchema);
      setNewPropertyName('');
      setNewPropertyType('string');
      setIsModalVisible(false);
      setExpandedKeys([...expandedKeys, newPropertyName]);
    }
  };

  const toggleExpanded = (key: string) => {
    setExpandedKeys(
      expandedKeys.includes(key)
        ? expandedKeys.filter((k) => k !== key)
        : [...expandedKeys, key],
    );
  };

  const getTypeInfo = (type: string) => {
    return typeOptions.find((opt) => opt.value === type) || typeOptions[0];
  };

  return (
    <StyledCard
      title="Schema Properties"
      extra={
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => setIsModalVisible(true)}
        >
          Add Field
        </Button>
      }
    >
      {Object.keys(properties).length === 0 ? (
        <EmptyContainer>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No fields defined yet"
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsModalVisible(true)}
            >
              Add Your First Field
            </Button>
          </Empty>
        </EmptyContainer>
      ) : (
        <div>
          {Object.entries(properties).map(([name, schema]) => {
            const typeInfo = getTypeInfo(
              Array.isArray(schema.type)
                ? schema.type[0]
                : schema.type || 'string',
            );
            const isRequired = required.includes(name);
            const isExpanded = expandedKeys.includes(name);

            return (
              <PropertyItem key={name}>
                <PropertyHeader onClick={() => toggleExpanded(name)}>
                  <Space>
                    <TypeBadge color={typeInfo.color}>
                      {typeInfo.label}
                    </TypeBadge>
                    <Text strong>{name}</Text>
                    {isRequired && (
                      <RequiredBadge color="red">Required</RequiredBadge>
                    )}
                  </Space>
                  <Space>
                    {isExpanded ? <UpOutlined /> : <DownOutlined />}
                  </Space>
                </PropertyHeader>

                {isExpanded && (
                  <PropertyContent>
                    <Form layout="vertical" size="small">
                      <Form.Item label="Description">
                        <TextArea
                          rows={2}
                          value={schema.description || ''}
                          onChange={(e) =>
                            onUpdate(name, {
                              ...schema,
                              description: e.target.value,
                            })
                          }
                          placeholder="Describe this field..."
                        />
                      </Form.Item>

                      <Form.Item label="Required">
                        <Switch
                          checked={isRequired}
                          onChange={() => onToggleRequired(name)}
                        />
                      </Form.Item>

                      {schema.type === 'string' && (
                        <>
                          <Form.Item label="Format">
                            <Select
                              value={schema.format || ''}
                              onChange={(value) =>
                                onUpdate(name, {
                                  ...schema,
                                  format: value || undefined,
                                })
                              }
                              style={{ width: '100%' }}
                            >
                              {stringFormatOptions.map((opt) => (
                                <Select.Option
                                  key={opt.value}
                                  value={opt.value}
                                >
                                  {opt.label}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>

                          <Form.Item
                            label="Pattern"
                            extra={
                              <HelpText>Regular expression pattern</HelpText>
                            }
                          >
                            <Input
                              value={schema.pattern || ''}
                              onChange={(e) =>
                                onUpdate(name, {
                                  ...schema,
                                  pattern: e.target.value || undefined,
                                })
                              }
                              placeholder="e.g., ^[A-Z]+$"
                            />
                          </Form.Item>

                          <Space>
                            <Form.Item label="Min Length">
                              <InputNumber
                                min={0}
                                value={schema.minLength}
                                onChange={(value) =>
                                  onUpdate(name, {
                                    ...schema,
                                    minLength: value || undefined,
                                  })
                                }
                              />
                            </Form.Item>

                            <Form.Item label="Max Length">
                              <InputNumber
                                min={0}
                                value={schema.maxLength}
                                onChange={(value) =>
                                  onUpdate(name, {
                                    ...schema,
                                    maxLength: value || undefined,
                                  })
                                }
                              />
                            </Form.Item>
                          </Space>
                        </>
                      )}

                      {(schema.type === 'number' ||
                        schema.type === 'integer') && (
                        <Space>
                          <Form.Item label="Minimum">
                            <InputNumber
                              value={schema.minimum ?? null}
                              onChange={(value) =>
                                onUpdate(name, {
                                  ...schema,
                                  minimum: value ?? undefined,
                                })
                              }
                            />
                          </Form.Item>

                          <Form.Item label="Maximum">
                            <InputNumber
                              value={schema.maximum ?? null}
                              onChange={(value) =>
                                onUpdate(name, {
                                  ...schema,
                                  maximum: value ?? undefined,
                                })
                              }
                            />
                          </Form.Item>
                        </Space>
                      )}

                      {schema.type === 'array' && (
                        <Form.Item label="Item Type">
                          <Select
                            value={
                              Array.isArray(schema.items)
                                ? 'string'
                                : (schema.items?.type ?? 'string')
                            }
                            onChange={(value) =>
                              onUpdate(name, {
                                ...schema,
                                items: {
                                  type: value as JSONSchemaType['type'],
                                },
                              })
                            }
                            style={{ width: '100%' }}
                          >
                            {typeOptions.map((opt) => (
                              <Select.Option key={opt.value} value={opt.value}>
                                {opt.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      )}

                      <Form.Item>
                        <Button
                          danger
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => onDelete(name)}
                        >
                          Delete Field
                        </Button>
                      </Form.Item>
                    </Form>
                  </PropertyContent>
                )}
              </PropertyItem>
            );
          })}
        </div>
      )}

      <Modal
        title="Add New Field"
        open={isModalVisible}
        onOk={handleAddProperty}
        onCancel={() => setIsModalVisible(false)}
        okText="Add Field"
        okButtonProps={{
          disabled: !newPropertyName || !!properties[newPropertyName],
        }}
      >
        <Form layout="vertical">
          <Form.Item
            label="Field Name"
            required
            validateStatus={properties[newPropertyName] ? 'error' : ''}
            help={
              properties[newPropertyName]
                ? 'A field with this name already exists'
                : ''
            }
          >
            <Input
              value={newPropertyName}
              onChange={(e) => setNewPropertyName(e.target.value)}
              placeholder="fieldName"
            />
          </Form.Item>

          <Form.Item label="Field Type" required>
            <Select
              value={newPropertyType}
              onChange={setNewPropertyType}
              style={{ width: '100%' }}
            >
              {typeOptions.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  <Space>
                    <TypeBadge color={opt.color}>{opt.label}</TypeBadge>
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </StyledCard>
  );
};
